/**
 * Shared MULTILEADER primitive-geometry builder (Lane H / S9).
 *
 * Extracted from `../../dimensions/multileaderToPrimitives.ts` so the LEGACY
 * bag adapter and the TYPED adapter (`../typed/multileaderFromEntity.ts`)
 * share ONE geometry truth: both extract the same handful of raw fields
 * (from the JSON-encoded `EntityPreview.properties` bag vs. the canonical
 * `MultileaderEntity` respectively) and feed them here. Mirrors the
 * `dimensionToPrimitives.ts` / `../geom/dimensionGeometry.ts` split.
 *
 * # Why `multileaderFromEntity` is NOT in the `entityToRenderableTyped`
 *   switch (unlike every other typed adapter)
 *
 * Block-content MLEADERs (`contentType === 1`) flatten through
 * `flattenBlockReference` (`../typed/blockFromEntity.ts`), which — for the
 * block's OWN children — dispatches back through `entityToRenderableTyped`
 * (`../typed/index.ts`). Registering `multileaderFromEntity` IN that same
 * switch would close an import cycle: `typed/index.ts` → `multileaderFrom
 * Entity.ts` → `multileaderGeometry.ts` (this file) → `typed/blockFrom
 * Entity.ts` → `typed/index.ts`. `typed/blockFromEntity.ts`'s own doc
 * comment documents the identical constraint for why "Block" itself is
 * NOT in the switch either ("a barrel re-export would create an import
 * cycle, forbidden by the dependency-cruiser zero-cycle gate"). Multileader
 * is dispatched explicitly instead — see `typedFirstRenderable.ts`'s
 * `EXPLICIT_DISPATCH_RENDER_KINDS` — still a real typed adapter (no bag
 * round-trip), just not reachable via the shared per-kind switch.
 *
 * Codex PR #784 round 4 F1 — this file no longer IMPORTS the flatten at all:
 * `MultileaderGeometryArgs.flattenBlockContent` is injected by the caller
 * (`../typed/blockReferenceFlatten.ts` documents why). That removed the
 * `multileaderGeometry → blockFromEntity` edge above, which is what lets
 * `blockFromEntity.ts` import `multileaderFromEntity` for its block-child
 * MULTILEADER arm (an INSERT of a definition containing an MLEADER used to
 * drop the whole callout). The `typed/index.ts` dispatch stays explicit —
 * that edge would re-close the cycle through the new import.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * Leader hierarchy walk (every root → node → line, break-agnostic —
 * matches the pre-extraction bag walker, which never read `LeaderLine`
 * breaks either), dogleg/landing geometry, spline vs. straight leader-line
 * dispatch, and block/MText content emission are byte-identical to the
 * pre-extraction inline code — a pure behaviour-preserving extraction.
 */

import {
  mapBlockAttributeReferences,
  type BlockAttrRefLike,
  type BlockDefinition,
  type BlockInsertAttribute,
} from "@dxf-ai-editor/drawing-model";

// Re-exported (not just imported) — `multileaderFromEntity.ts` and
// `multileaderToPrimitives.ts` both import `mapBlockAttributeReferences`/`BlockAttrRefLike` FROM
// this file (see the dup:check-convergence note near the old definition site below); barrel
// re-export keeps their import paths unchanged (CLAUDE.md split-pattern convention) even though
// the implementation itself now lives in `@dxf-ai-editor/drawing-model`.
export { mapBlockAttributeReferences, type BlockAttrRefLike };

import { applyOffset } from "../../coordinateOrigin";
import { parseMTextContent } from "../../text/mtextParser";
import type {
  MultileaderPrimitive,
  SceneGeometry,
  Vec3,
} from "../../types";
import { dimblkToVariant } from "../../dimensions/arrowMarkerMesh";
import { resolveInsertLinetypeTyped } from "../typed/insertLinetype";
import type { BlockContentFlattener } from "../typed/blockReferenceFlatten";
import type { AdapterContext } from "../adapterContext";
import type { RenderableEntity } from "../../types";
import { resolveByLayerLinetypeName } from "../../rendering/linetypeResolve";
import { unitVec2, rotatedBoxCorners, type RawVec2 } from "./vecMath";
import { splitLeaderLineAtBreaks, type LeaderBreak } from "./leaderBreaks";

function vec3(x: number, y: number, z: number = 0): Vec3 {
  return { x, y, z };
}

export interface MultileaderBbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function emptyBbox(): MultileaderBbox {
  return {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };
}

function extend(bbox: MultileaderBbox, p: Vec3): void {
  if (p.x < bbox.minX) bbox.minX = p.x;
  if (p.y < bbox.minY) bbox.minY = p.y;
  if (p.x > bbox.maxX) bbox.maxX = p.x;
  if (p.y > bbox.maxY) bbox.maxY = p.y;
}

/**
 * Codex S9b P2 ("Preserve block child ByLayer linetypes") — bake a flattened
 * MLEADER block-content child's `ByLayer` linetype sentinel into the concrete
 * linetype NAME assigned to the child's OWN layer, so the downstream re-home
 * onto the parent MLEADER layer (`collectRenderSatellites`) cannot make
 * the bucket resolve it against the wrong (parent) layer. A child that already
 * carries a concrete name (or `ByBlock`, already resolved to `Continuous` at
 * flatten) is returned unchanged. Absent layer table / unknown layer → the
 * layer's own default `Continuous` (matches the ByLayer bucket redirect).
 */
export function bakeChildByLayerLinetype(
  child: RenderableEntity,
  ctx: AdapterContext,
): RenderableEntity {
  const lt = (child.linetype ?? "").trim().toLowerCase();
  if (lt !== "" && lt !== "bylayer") return child;
  return {
    ...child,
    linetype: resolveByLayerLinetypeName(child.layer, ctx.layerLinetypes),
  };
}

/* ── Leader hierarchy raw shape (bag-decoded JSON and typed model both map
   into this before the walk) ────────────────────────────────────────── */

export interface RawLeaderLine {
  vertices: RawVec2[];
  arrowheadBlock: string;
  /**
   * S9b — LeaderLine break ranges (ezdxf `LeaderLine.breaks`). Each range
   * cuts a gap in the leader stroke (start → end). Both adapters map their
   * source breaks (bag `EncodedLeaderLine.b` quads / typed `line.breakPoints`)
   * into this shape; the shared walker splits the stroke via
   * `splitLeaderLineAtBreaks` so the WebGL leader honours the gap the SVG
   * renderer already drew (Codex S9b P2). Optional/additive — an omitted or
   * empty array means an unbroken leader (the overwhelmingly common case).
   */
  breaks?: LeaderBreak[];
}
export interface RawLeaderNode {
  doglegVector: RawVec2 | null;
  lines: RawLeaderLine[];
}
export interface RawLeaderRoot {
  nodes: RawLeaderNode[];
}

/**
 * Raw resolved (entity-vs-style-flag-gated, for the bag path; direct
 * typed-field reads, for the typed path) MLEADER inputs the shared walker
 * needs.
 */
export interface MultileaderRawFields {
  leaderRoots: RawLeaderRoot[];
  leaderType: number;
  /** Entity-level `arrowheadBlock` — per-line `RawLeaderLine.arrowheadBlock
   *  || arrowheadBlockFallback` mirrors the legacy `line.a || arrowheadBlock`. */
  arrowheadBlockFallback: string;
  arrowheadSize: number;
  landingEnabled: boolean;
  doglegEnabled: boolean;
  doglegLength: number;
  landingGap: number;
  contentType: number;
  textHeight: number;
  textRotation: number;
  /**
   * GitHub Codex review PR #558 (post-rebase fresh pass) P2 — MTEXT-content
   * glyph-handedness companion to `textRotation` (`MultileaderEntity.
   * textMirrored`'s doc comment). Threaded straight into the `mtext-content`
   * primitive's own `mirrored` field.
   */
  textMirrored: boolean;
  /** `"ByLayer"` / `"ByBlock"` / an explicit hex string. */
  textColorRaw: string;
  /**
   * MLEADER block-content colour (DXF group 93 / MLEADERSTYLE block colour).
   * `"ByLayer"` / `"ByBlock"` / an explicit hex string. AutoCAD applies it as
   * the BYBLOCK colour of the referenced block's geometry — the SVG twin
   * (`renderMultileader.tsx`) renders the block content group with
   * `stroke={blockColor}`. Threaded into the flatten context so BYBLOCK
   * children resolve to it (Codex S9b P2).
   */
  blockColor: string;
  blockContentName: string;
  blockScale: number;
  blockRotation: number;
  textLocationRaw: RawVec2;
  contents: string;
}

export interface MultileaderGeometryArgs {
  raw: MultileaderRawFields;
  colorHex: string;
  /**
   * The MLEADER entity's OWN resolved layer. Threaded as the block-content
   * flatten's `parentLayer` so a referenced block's layer-"0" children inherit
   * the MLEADER's layer (AutoCAD block-layer semantics), exactly like a normal
   * INSERT's layer-0 children inherit the INSERT's layer — Codex round-6 P2
   * ("Inherit the MLEADER layer for layer-0 block children"). Without it the
   * flatten resolves + bakes those children's colour / linetype against layer
   * "0" before `collectRenderSatellites` re-homes them onto the MLEADER
   * layer, so they'd render with layer-0 styling.
   */
  layer: string;
  /**
   * The MLEADER entity's OWN raw linetype token (`"ByLayer"` / `"ByBlock"` / an
   * explicit LTYPE name / undefined). Resolved via `resolveInsertLinetypeTyped`
   * and threaded as the block-content flatten's `ctx.blockLinetype` so a BYBLOCK
   * child of the referenced block inherits the MLEADER's linetype — exactly like
   * a normal INSERT threads its resolved linetype to its BYBLOCK children
   * (`blockFromEntity.ts`), the linetype twin of `blockColor` (Codex round-7 P2
   * "Thread the MLEADER linetype into block-content flattening"). Absent → the
   * MLEADER resolves ByLayer against `layer` — the correct real-world default.
   */
  linetypeRaw?: string;
  attributes: readonly BlockInsertAttribute[];
  blocks: Record<string, BlockDefinition>;
  ctx: AdapterContext;
  spaceId: string;
  parentEntityId: string;
  /**
   * Codex PR #784 round 4 F1 — the block-content flatten, INJECTED rather
   * than imported (module doc). REQUIRED on purpose: an optional/defaulted
   * flattener would let a new call site silently drop a `contentType === 1`
   * callout's block graphics, the exact defect this inversion closes.
   * Production callers pass `flattenBlockReference`
   * (`../typed/blockFromEntity.ts`) verbatim; the block-child arm passes a
   * closure carrying the enclosing flatten's recursion guard.
   */
  flattenBlockContent: BlockContentFlattener;
}

interface EmitResult {
  primitives: MultileaderPrimitive[];
  bbox: MultileaderBbox;
}

// `BlockAttrRefLike`/`mapBlockAttributeReferences` moved to `packages/drawing-model` (@codex T1
// arm(c) round-19 dup:check convergence — `wcsBounds.ts` needed the SAME algorithm for the bounds
// path (F9) and, unlike the app→package-blocked duplications elsewhere in this file family,
// `apps/web` already depends on `packages/drawing-model`, so this one converges to a single
// shared source instead of a second copy). See `@dxf-ai-editor/drawing-model`'s
// `wcsBoundsMath.ts::mapBlockAttributeReferences` for the full doc.

/** Build the final `SceneGeometry` envelope from the emitter's primitives. */
export function toMultileaderSceneGeometry(
  primitives: MultileaderPrimitive[],
): SceneGeometry {
  return {
    kind: "multileader",
    primitives,
    // Pre-existing gap (both paths): the bag never carried a per-multileader
    // "Style" key either — reproduced verbatim for golden parity.
    styleName: "Standard",
  };
}

/**
 * Walk every leader root → node → line, emit dogleg/landing, and emit the
 * MText / block content, returning the full primitive list + bbox. Both
 * `multileaderToPrimitives` (bag) and `multileaderFromEntity` (typed) call
 * this with their own extracted {@link MultileaderRawFields}.
 */
export function buildMultileaderGeometry(args: MultileaderGeometryArgs): EmitResult {
  const { raw, colorHex } = args;
  const textLocation = applyOffset(vec3(raw.textLocationRaw.x, raw.textLocationRaw.y));
  const textColorHex =
    raw.textColorRaw === "ByLayer" || raw.textColorRaw === "ByBlock" || raw.textColorRaw === ""
      ? colorHex
      : raw.textColorRaw;

  const primitives: MultileaderPrimitive[] = [];
  const bbox = emptyBbox();

  for (const root of raw.leaderRoots) {
    if (!root) continue;
    for (const node of root.nodes) {
      if (!node) continue;
      for (let li = 0; li < node.lines.length; li++) {
        const line = node.lines[li];
        if (!line) continue;
        let verts: Vec3[] = line.vertices.map((v) => applyOffset(vec3(v.x, v.y)));
        if (verts.length === 0) continue;
        if (verts.length === 1) {
          verts = [...verts, textLocation];
        }
        const firstArrow = line.arrowheadBlock || raw.arrowheadBlockFallback;

        if (raw.leaderType !== 0 && verts.length >= 2) {
          // S9b — split the leader stroke at its break ranges (Codex P2:
          // "Preserve MLEADER break gaps in WebGL"). Each surviving sub-run
          // becomes its own `leader-line` primitive so the batched-line
          // pipeline draws the gap the SVG renderer already honoured. An
          // unbroken leader yields exactly one sub-run == the original verts.
          //
          // The break points, like the vertices, are RAW DXF coords — offset
          // them through `applyOffset` into the same offset-world space the
          // `verts` already live in, else the nearest-segment match + the
          // inserted gap endpoints would be off by the per-import origin.
          const offsetBreaks: LeaderBreak[] = (line.breaks ?? []).map((b) => {
            const s = applyOffset(vec3(b.start.x, b.start.y));
            const e = applyOffset(vec3(b.end.x, b.end.y));
            return { start: { x: s.x, y: s.y }, end: { x: e.x, y: e.y } };
          });
          const subRuns = splitLeaderLineAtBreaks(
            verts,
            offsetBreaks,
            (x, y) => vec3(x, y),
          );
          for (const sub of subRuns) {
            if (sub.length < 2) continue;
            primitives.push({
              kind: "leader-line",
              vertices: sub,
              spline: raw.leaderType === 2,
              colorHex,
            });
            for (const v of sub) extend(bbox, v);
          }

          const v0 = verts[0]!;
          const v1 = verts[1]!;
          const dir = unitVec2(v0.x - v1.x, v0.y - v1.y);
          if (firstArrow !== "_NONE") {
            primitives.push({
              kind: "arrow",
              tip: v0,
              direction: vec3(dir.x, dir.y),
              size: raw.arrowheadSize,
              variant: dimblkToVariant(firstArrow),
              colorHex,
            });
          }
        }

        const last = verts[verts.length - 1];
        if (raw.landingEnabled && raw.doglegEnabled && li === node.lines.length - 1 && last) {
          let endX: number;
          let endY: number;
          if (node.doglegVector) {
            const dx = node.doglegVector.x;
            const dy = node.doglegVector.y;
            const mag = Math.hypot(dx, dy) || 1;
            endX = last.x + (dx / mag) * raw.doglegLength;
            endY = last.y + (dy / mag) * raw.doglegLength;
          } else {
            const sign = textLocation.x >= last.x ? 1 : -1;
            endX = last.x + sign * raw.doglegLength;
            endY = last.y;
          }
          const doglegEnd = vec3(endX, endY);
          primitives.push({ kind: "dogleg", a: last, b: doglegEnd, colorHex });
          extend(bbox, doglegEnd);
          const dirToContent = unitVec2(textLocation.x - doglegEnd.x, textLocation.y - doglegEnd.y);
          const landingEnd = vec3(
            textLocation.x - dirToContent.x * raw.landingGap,
            textLocation.y - dirToContent.y * raw.landingGap,
          );
          primitives.push({ kind: "landing", a: doglegEnd, b: landingEnd, colorHex });
          extend(bbox, landingEnd);
        }
      }
    }
  }

  if (raw.contentType === 2) {
    const runs = parseMTextContent(raw.contents, {
      fontStyleName: "Standard",
      heightWorld: raw.textHeight,
      widthFactor: 1,
      colorHex: textColorHex,
    });
    primitives.push({
      kind: "mtext-content",
      anchor: textLocation,
      rotation: raw.textRotation,
      height: raw.textHeight,
      runs,
      lineSpacingFactor: 1,
      attachmentPoint: 1,
      styleName: "Standard",
      colorHex: textColorHex,
      mirrored: raw.textMirrored,
    });
    // GitHub Codex review PR #558 round 2 (round-3 local read-only
    // follow-up) P2 — a nonzero `textRotation` now actually rotates the
    // rendered `mtext-content` primitive, so the bbox must follow: extend
    // it with the text box's ROTATED corners, not a horizontal
    // axis-aligned box. A content-only MLeader with a long run rotated to
    // 90° would otherwise render vertically far outside its reported
    // bounds, dropping out of spatial-index culling and pick/window
    // selection. `raw.textRotation` is DEGREES at this render-feed layer
    // (both `multileaderFromEntity.ts` and `multileaderToPrimitives.ts`
    // convert the typed model's RADIANS `MultileaderEntity.textRotation`
    // to degrees before constructing `MultileaderRawFields` — see either
    // file's `textRotation` doc comment) — convert to radians for
    // `rotatedBoxCorners`. The local box is Y-SYMMETRIC around the anchor
    // (`-textHeight`..`+textHeight`) — mirroring is a no-op on its extent,
    // so (unlike Dimension's asymmetric box) no mirrored-sign handling is
    // needed here.
    const longest = raw.contents.split(/\\P|\n/g).reduce((m, l) => Math.max(m, l.length), 0);
    const halfX = Math.max(longest * raw.textHeight * 0.6, raw.textHeight) * 0.5;
    const corners = rotatedBoxCorners(
      textLocation,
      -halfX,
      halfX,
      -raw.textHeight,
      raw.textHeight,
      (raw.textRotation * Math.PI) / 180,
    );
    for (const c of corners) extend(bbox, vec3(c.x, c.y));
  } else if (raw.contentType === 1 && raw.blockContentName) {
    // Codex S9b P2 ("Thread MLEADER block colors into flattened children"):
    // the MLEADER's block-content colour (group 93) is the BYBLOCK colour of
    // the referenced block's geometry. ByLayer/ByBlock/empty → the MLEADER's
    // own resolved colour (mirrors the SVG twin's `currentColor` fallback +
    // the `textColorHex` chain above); an explicit hex passes through. Thread
    // it as `ctx.blockColor` so a BYBLOCK child resolves to it instead of
    // falling back to its own layer/default — exactly how a real INSERT threads
    // its resolved colour to its children (`blockFromEntity.ts`).
    const blockColorHex =
      raw.blockColor === "ByLayer" ||
      raw.blockColor === "ByBlock" ||
      raw.blockColor === ""
        ? colorHex
        : raw.blockColor;
    // Codex round-7 P2 ("Thread the MLEADER linetype into block-content
    // flattening"): the linetype twin of the `blockColor` thread above. A
    // BYBLOCK-linetype child of the referenced block resolves its sentinel from
    // `ctx.blockLinetype` (`resolveLinetypeName`) — without threading it, such a
    // child degrades to `Continuous` instead of inheriting the callout's dashed/
    // center linetype. `resolveInsertLinetypeTyped` resolves the MLEADER's OWN
    // linetype (ByLayer → its layer's linetype; explicit → passthrough;
    // Continuous/empty → null so a BYBLOCK child degrades to solid), exactly how
    // a real INSERT threads its resolved linetype to its BYBLOCK children
    // (`blockFromEntity.ts`). `null` correctly leaves BYBLOCK children solid.
    const blockLinetype = resolveInsertLinetypeTyped(
      args.linetypeRaw,
      args.layer,
      args.ctx,
    );
    const rawFlattened = args.flattenBlockContent({
      blockName: raw.blockContentName,
      // Codex round-8 P2 ("Use raw MLEADER insertion for block flattening"):
      // `textLocation` is ALREADY origin-offset (`applyOffset` at the top of
      // `buildMultileaderGeometry`). `flattenBlockReference` folds the
      // insertion into each child's transform BEFORE the per-kind typed
      // adapters apply `applyOffset` exactly ONCE (the `blockFromEntity`
      // synthetic-reference contract expects a RAW insertion). Passing the
      // already-offset `textLocation` here therefore SUBTRACTS the coordinate
      // origin a second time, landing the block content one origin vector away
      // from the leader/text anchor on large-coordinate imports. Pass the RAW
      // (un-offset) `textLocationRaw` so the child adapters' single `applyOffset`
      // yields the correct single-offset position — identical to how a real
      // INSERT folds its raw insertion before the per-kind `applyOffset`
      // (`blockFromEntity.ts` "offset-space equivalence"). The `block-content`
      // primitive's own `anchor` below keeps the offset `textLocation`: it lives
      // in offset-world space alongside the leader vertices / MText anchor.
      insertion: vec3(raw.textLocationRaw.x, raw.textLocationRaw.y),
      scale: vec3(raw.blockScale, raw.blockScale, 1),
      rotationDeg: raw.blockRotation,
      spaceId: args.spaceId,
      parentEntityId: args.parentEntityId,
      blocks: args.blocks,
      ctx: { ...args.ctx, blockColor: blockColorHex, blockLinetype },
      attributes: args.attributes,
      // Codex round-6 P2 ("Inherit the MLEADER layer for layer-0 block
      // children"): the MLEADER's layer is the effective enclosing layer for its
      // block content, so a referenced-block child authored on layer "0"
      // inherits it (`resolveBlockChildLayer`) — its ByLayer colour / linetype
      // then bake against the MLEADER layer, not the default "0". Non-"0"
      // children keep their own layer (the resolver is a no-op for them).
      parentLayer: args.layer,
    });
    // Codex S9b P2 ("Preserve block child ByLayer linetypes"): downstream
    // (`collectRenderSatellites`) RE-HOMES every child onto the parent
    // MLEADER's layer so the whole callout hides in lockstep. Colour is already
    // BAKED to a concrete hex at flatten (`resolveColorHex` resolves ByLayer →
    // the child layer's hex), but linetype is left as the deferred `ByLayer`
    // sentinel — resolved against the BUCKET layer at bucket-key time. After the
    // re-home that bucket layer is the PARENT's, so a still-`ByLayer` child would
    // wrongly inherit the parent layer's linetype (e.g. render Continuous where
    // its own layer is Hidden, or vice-versa). Bake it HERE against the child's
    // OWN layer so the re-home cannot corrupt it — mirroring the colour bake.
    const flattened = rawFlattened.map((f) => bakeChildByLayerLinetype(f, args.ctx));
    primitives.push({
      kind: "block-content",
      blockName: raw.blockContentName,
      anchor: textLocation,
      scale: raw.blockScale,
      rotation: raw.blockRotation,
      colorHex,
      flattened,
    });
    for (const f of flattened) {
      extend(bbox, vec3(f.bounds.minX, f.bounds.minY));
      extend(bbox, vec3(f.bounds.maxX, f.bounds.maxY));
    }
  }

  if (!Number.isFinite(bbox.minX)) {
    bbox.minX = textLocation.x;
    bbox.minY = textLocation.y;
    bbox.maxX = textLocation.x;
    bbox.maxY = textLocation.y;
  }

  return { primitives, bbox };
}
